import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { RootState } from "../../store";
import { toast } from "sonner";
import { getUser } from "../../helpers/user";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Button";
import Input from "../../components/Inputs";
import BaseLayout from "../../components/Layout";

import { ativar2FA, resetAtivar2FAState } from "../../features/2fa/slices";
import { verificarEAtivar2FA } from "../../features/2fa/validar-2fa/slices";

import "./_styles.scss";

const PageUserConfig = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = getUser();

  const { status: statusGerar, setupData } = useSelector(
    (state: RootState) => state.ativar2fa,
  );
  const { status: statusValidar } = useSelector(
    (state: RootState) => state.verificar2fa,
  );

  const [profileData, setProfileData] = useState({
    login: user.usuario.login || "",
    email: user.usuario.email || "",
    contato: user.usuario.contato || "",
  });

  const [verificationCode, setVerificationCode] = useState("");
  const [isSuccessfullyActivated, setIsSuccessfullyActivated] = useState(false);

  const handleProfileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handleAtivarClick = () => {
    dispatch(ativar2FA());
  };

  const handleConfirmarAtivacao = async () => {
    if (!/^\d{6}$/.test(verificationCode)) {
      toast.error("Insira o código de 6 dígitos.");
      return;
    }

    try {
      await dispatch(
        verificarEAtivar2FA({
          code: verificationCode,
          secretKey: setupData?.manualEntryKey || "",
        }),
      ).unwrap();

      toast.success("2FA ativado com sucesso!");
      setIsSuccessfullyActivated(true);
      setVerificationCode("");
    } catch (err: any) {
      toast.error(err || "Código inválido. Tente novamente.");
    }
  };

  const handleCancelar = () => {
    dispatch(resetAtivar2FAState());
    setVerificationCode("");
  };

  const hasAlreadyActive2FA =
    user.usuario.chave2fa !== null || isSuccessfullyActivated;
  const isSettingUp =
    statusGerar === "success" && setupData && !isSuccessfullyActivated;

  return (
    <BaseLayout>
      <div className="security-page-wrapper">
        <div className="security-card">
          <header className="card-header">
            <h2>Dados da Conta</h2>
            <p className="card-description">
              Gerencie suas informações de acesso.
            </p>
          </header>
          <div className="card-content">
            <div className="profile-grid">
              <Input
                label="Login"
                name="login"
                value={profileData.login}
                disabled
                onChange={handleProfileChange}
              />
            </div>
            {/* <div className="mt-20">
              <Button size="sm" onClick={() => toast.info("Em breve...")}>Salvar Alterações</Button>
            </div> */}
          </div>
        </div>

        <div className="security-card">
          <header className="card-header">
            <h2>Segurança</h2>
            <p className="card-description">
              Proteja sua conta com 2FA e senha forte.
            </p>
          </header>

          <div className="card-content">
            <div className="password-row mb-30">
              <div className="text-info">
                <h3>Senha de Acesso</h3>
                <p>Altere sua senha periodicamente.</p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/Forgot-Pass")}
              >
                Alterar Senha
              </Button>
            </div>

            <div className="two-factor-section">
              <h3>Autenticação de Dois Fatores (2FA)</h3>

              {hasAlreadyActive2FA ? (
                <div className="state-success-centered fade-in">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="icon-checked-green"
                  >
                    <circle cx="12" cy="12" r="12" fill="#10B981" />
                    <path
                      d="M17.25 7.75L9.75 15.25L6.75 12.25"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="success-message">
                    O 2FA está ativo em sua conta.
                  </p>
                </div>
              ) : (
                <>
                  {statusGerar === "idle" && (
                    <div className="state-idle fade-in">
                      <p>O 2FA adiciona uma camada extra de proteção.</p>
                      <Button
                        size="md"
                        variant="primary"
                        onClick={handleAtivarClick}
                      >
                        Ativar Agora
                      </Button>
                    </div>
                  )}

                  {statusGerar === "loading" && (
                    <div className="state-loading">
                      <div className="spinner"></div>
                      <p>Gerando chaves...</p>
                    </div>
                  )}

                  {isSettingUp && (
                    <div className="state-setup fade-in">
                      <div className="setup-step">
                        <span className="step-badge">1</span>
                        <div className="step-content">
                          <p>Escaneie o QR Code no seu app autenticador.</p>
                          <div className="qr-container">
                            <img
                              src={setupData.qrCodeImageUrl}
                              alt="QR Code"
                              className="qr-image"
                            />
                          </div>
                          <div className="manual-key-container">
                            <span>Chave manual:</span>
                            <code className="manual-key">
                              {setupData.manualEntryKey}
                            </code>
                          </div>
                        </div>
                      </div>

                      <div className="divider"></div>

                      <div className="setup-step">
                        <span className="step-badge">2</span>
                        <div className="step-content">
                          <p>Digite o código gerado para confirmar.</p>
                          <input
                            type="text"
                            value={verificationCode}
                            onChange={(e) =>
                              setVerificationCode(
                                e.target.value.replace(/\D/g, ""),
                              )
                            }
                            placeholder="000000"
                            maxLength={6}
                            className="input-code"
                          />
                          <div className="action-buttons">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={handleCancelar}
                            >
                              Cancelar
                            </Button>
                            <Button
                              variant="primary"
                              onClick={handleConfirmarAtivacao}
                              size="sm"
                              loading={statusValidar === "loading"}
                            >
                              Confirmar Ativação
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default PageUserConfig;
